/**
 * Traçado de rota "andando" no sentido do desenho (stroke-dashoffset) e setas de sentido ao longo
 * da linha. Usado nos mapas da tela Histórico de rotas (abas Linhas/Unidades e Alterações).
 */
import L from 'leaflet';

/** Estilos das setas e do traçado "andando": injetados uma vez no <head> ao importar o módulo. */
const CSS_FLUXO_ROTA = `
.har-map-dir-marker {
    background: transparent !important;
    border: none !important;
    pointer-events: none !important;
}

.har-map-dir-arrow {
    display: flex;
    align-items: center;
    justify-content: center;
    transform-origin: 50% 50%;
}

/* Traçado “andando” no sentido da rota (stroke-dashoffset) */
.har-route-flow {
    animation: har-route-dash-flow 4.2s linear infinite;
}

.har-route-flow--original {
    /* dash 14 + gap 22 = 36 */
    animation-duration: 4.6s;
}

.har-route-flow--selected {
    /* dash 16 + gap 24 = 40 */
    animation-duration: 4s;
}

@keyframes har-route-dash-flow {
    from {
        stroke-dashoffset: 0;
    }
    to {
        /* negativo = flui no sentido do desenho do path */
        stroke-dashoffset: -40;
    }
}

@media (prefers-reduced-motion: reduce) {
    .har-route-flow {
        animation: none !important;
    }
}
`;

if (typeof document !== 'undefined' && !document.getElementById('mapa-fluxo-rota-css')) {
    const style = document.createElement('style');
    style.id = 'mapa-fluxo-rota-css';
    style.textContent = CSS_FLUXO_ROTA;
    document.head.appendChild(style);
}

const MAX_DIRECTION_ARROWS = 36;
/** Ângulo mínimo (°) entre segmentos para considerar vértice em curva. */
const CURVE_TURN_DEG = 7;
/** Quantidade de curvas significativas para ativar amostragem procedural. */
const CURVY_ROUTE_MIN_TURNS = 4;
/** Máximo de segmentos retos sem seta (gap de segurança). */
const MAX_STRAIGHT_GAP = 18;
/** Acúmulo de curva (°) desde a última seta para forçar nova seta no trecho curvo. */
const ACCUM_TURN_PLACE_DEG = 14;

/** Classes do traçado animado: a versão de referência (anterior/original) e a selecionada. */
export const FLUXO_ORIGINAL = 'har-route-flow har-route-flow--original';
export const FLUXO_SELECIONADA = 'har-route-flow har-route-flow--selected';

/** Traço tracejado que anda: dash 14 + gap 22 (referência) e 16 + 24 (selecionada). */
export function estiloFluxoOriginal(color) {
    return { color, weight: 4, opacity: 0.9, dashArray: '14 22', lineCap: 'round', lineJoin: 'round' };
}

export function estiloFluxoSelecionada(color) {
    return { color, weight: 5, opacity: 0.95, dashArray: '16 24', lineCap: 'round', lineJoin: 'round' };
}

/** Linha contínua e translúcida por baixo do tracejado, para o trajeto não "sumir" nos vãos. */
export function estiloFundoRota(color, weight) {
    return { color, weight, opacity: 0.28, lineCap: 'round', lineJoin: 'round' };
}

/** Options do l-geo-json: estilo do path e, se informada, a classe CSS da animação. */
export function buildRouteLayerOptions(style, flowClassName) {
    const pathStyle = { ...(style || {}) };
    delete pathStyle.className;
    return {
        style: () => ({ ...pathStyle }),
        onEachFeature: (feature, layer) => {
            if (!flowClassName || !layer) return;
            if (layer.options) {
                layer.options.className = flowClassName;
            }
            const applyClass = () => {
                if (layer._path) {
                    L.DomUtil.addClass(layer._path, flowClassName);
                }
            };
            applyClass();
            layer.on('add', applyClass);
        },
    };
}

function bearingGraus(lat1, lng1, lat2, lng2) {
    const φ1 = (lat1 * Math.PI) / 180;
    const φ2 = (lat2 * Math.PI) / 180;
    const Δλ = ((lng2 - lng1) * Math.PI) / 180;
    const y = Math.sin(Δλ) * Math.cos(φ2);
    const x = Math.cos(φ1) * Math.sin(φ2) - Math.sin(φ1) * Math.cos(φ2) * Math.cos(Δλ);
    const θ = Math.atan2(y, x);
    return ((θ * 180) / Math.PI + 360) % 360;
}

function angleDiffDeg(a, b) {
    let d = Math.abs(Number(a) - Number(b)) % 360;
    if (d > 180) d = 360 - d;
    return d;
}

function extractLatLngsFromGeojson(geojson) {
    const out = [];
    const pushPair = (lng, lat) => {
        const la = Number(lat);
        const ln = Number(lng);
        if (!Number.isFinite(la) || !Number.isFinite(ln)) return;
        out.push([la, ln]);
    };
    const walkCoords = (coords, depth) => {
        if (!Array.isArray(coords) || !coords.length) return;
        if (typeof coords[0] === 'number') {
            pushPair(coords[0], coords[1]);
            return;
        }
        // LineString: [[lng,lat],...]; Polygon ring / Multi*: nested
        if (depth <= 1 && typeof coords[0][0] === 'number') {
            coords.forEach((c) => pushPair(c[0], c[1]));
            return;
        }
        coords.forEach((c) => walkCoords(c, depth + 1));
    };
    const visit = (gj) => {
        if (!gj) return;
        if (gj.type === 'FeatureCollection') {
            (gj.features || []).forEach(visit);
            return;
        }
        if (gj.type === 'Feature') {
            visit(gj.geometry);
            return;
        }
        if (!gj.type || !gj.coordinates) return;
        if (gj.type === 'Point') {
            pushPair(gj.coordinates[0], gj.coordinates[1]);
            return;
        }
        if (gj.type === 'LineString') {
            walkCoords(gj.coordinates, 0);
            return;
        }
        if (gj.type === 'MultiLineString') {
            (gj.coordinates || []).forEach((line) => walkCoords(line, 0));
            return;
        }
        // Polygons are fences, not route direction — skip
    };
    visit(geojson);
    return out;
}

/** Posições e rumos das setas de sentido: esparsas nas retas, mais densas nas curvas. */
export function buildDirectionMarkers(geojson) {
    const latlngs = extractLatLngsFromGeojson(geojson);
    const out = [];
    if (!latlngs || latlngs.length < 2) return out;

    const segments = [];
    for (let i = 0; i < latlngs.length - 1; i += 1) {
        const [la1, ln1] = latlngs[i];
        const [la2, ln2] = latlngs[i + 1];
        const bearing = bearingGraus(la1, ln1, la2, ln2);
        if (bearing == null || Number.isNaN(bearing)) continue;
        segments.push({
            i,
            bearing: ((bearing % 360) + 360) % 360,
            lat: (la1 + la2) / 2,
            lng: (ln1 + ln2) / 2,
            turn: 0,
        });
    }
    if (segments.length === 0) return out;

    for (let i = 1; i < segments.length; i += 1) {
        segments[i].turn = angleDiffDeg(segments[i - 1].bearing, segments[i].bearing);
    }

    const curveCount = segments.filter((s) => s.turn >= CURVE_TURN_DEG).length;
    const useCurveSampling = curveCount >= CURVY_ROUTE_MIN_TURNS;

    if (!useCurveSampling) {
        const step = Math.max(1, Math.ceil(segments.length / MAX_DIRECTION_ARROWS));
        for (let i = 0; i < segments.length; i += step) {
            const s = segments[i];
            out.push({ lat: s.lat, lng: s.lng, bearing: s.bearing });
        }
        return out;
    }

    // Procedural: densifica setas nos trechos em curva; retas ficam esparsas.
    let lastPlaced = -Infinity;
    let accumTurn = 0;
    const pushSeg = (s, idx) => {
        out.push({ lat: s.lat, lng: s.lng, bearing: s.bearing });
        lastPlaced = idx;
        accumTurn = 0;
    };

    // Sempre uma seta no início para ancorar o sentido.
    pushSeg(segments[0], 0);

    for (let i = 1; i < segments.length; i += 1) {
        if (out.length >= MAX_DIRECTION_ARROWS) break;
        const s = segments[i];
        accumTurn += s.turn;
        const gap = i - lastPlaced;
        const onCurve = s.turn >= CURVE_TURN_DEG;
        const sharpCurve = s.turn >= CURVE_TURN_DEG * 2;
        const shouldPlace = onCurve
            ? (sharpCurve && gap >= 1)
                || (accumTurn >= ACCUM_TURN_PLACE_DEG && gap >= 1)
                || (gap >= 3 && s.turn >= CURVE_TURN_DEG)
            : gap >= MAX_STRAIGHT_GAP;

        if (shouldPlace) {
            pushSeg(s, i);
        }
    }

    // Garante seta perto do fim se o gap final for grande.
    const last = segments[segments.length - 1];
    if (
        out.length < MAX_DIRECTION_ARROWS
        && segments.length - 1 - lastPlaced >= Math.min(6, MAX_STRAIGHT_GAP / 2)
    ) {
        out.push({ lat: last.lat, lng: last.lng, bearing: last.bearing });
    }

    return out;
}

/** Ícone da seta de sentido, girado pelo rumo (bearing) do trecho. */
export function iconDirecaoRota(bearing, color) {
    const b = Number(bearing) || 0;
    const sz = 14;
    const html =
        `<div class="har-map-dir-arrow" style="transform:rotate(${b}deg);width:${sz}px;height:${sz}px;">`
        + `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="${sz}" height="${sz}" `
        + `fill="${color}" stroke="#ffffff" stroke-width="1.2" stroke-linejoin="round">`
        + `<path d="M12 3 L21 19 L12 15 L3 19 Z"/>`
        + `</svg></div>`;
    return L.divIcon({
        className: 'har-map-dir-marker',
        html,
        iconSize: [sz, sz],
        iconAnchor: [sz / 2, sz / 2],
    });
}
